import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wrench } from 'lucide-react';

import { ModeCard } from '../components/ui/ModeCard';
import { UseCaseCard } from '../components/ui/UseCaseCard';
import { useSalaAuth } from '../auth/SalaAuthContext';
import { RecipeAiBuildDialog } from '../studio/components/RecipeAiBuildDialog';
import {
  MultiToolActivityCta,
  MultiToolActivityDialog,
  type MultiToolActivityDialogResult,
} from '../studio/components/MultiToolActivityDialog';
import { listActivityDrafts, createDraftId, saveActivityDraft } from '../studio/activityStorage';
import {
  SUBJECT_CATALOG,
  getBuilderModesForSubject,
  getInteractiveUseCasesForSubject,
  getSubjectById,
  getSubjectLucideIcon,
  type AICaseRecipe,
} from '../studio/constants/subjectCatalog';
import type { RecipeAiBuildOptions } from '../studio/constants/recipeAiParams';
import { buildStudioMaterialUrl } from '../studio/constants/studioMaterialRoutes';
import {
  buildRecipePostGeneratePath,
  recipeUsesGuideBlockEditor,
  saveRecipeAsWrappedExam,
} from '../studio/recipeMaterialBridge';
import { generateMultiToolInteractiveClass } from '../studio/lib/multiToolActivityGeneration';
import { generateSubjectAwareActivity } from '../studio/studioActivityGeneration';

export function StudioToolsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useSalaAuth();
  const drafts = listActivityDrafts();

  const subjects = useMemo(
    () => SUBJECT_CATALOG.filter((subject) => subject.id !== 'general'),
    [],
  );

  const initialSubject =
    searchParams.get('subject') ||
    profile?.subjectId ||
    subjects[0]?.id ||
    'matematica';

  const [subjectId, setSubjectId] = useState(initialSubject);
  const [selectedRecipe, setSelectedRecipe] = useState<AICaseRecipe | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiGenerating, setMultiGenerating] = useState(false);

  React.useEffect(() => {
    const fromUrl = searchParams.get('subject');
    if (fromUrl && fromUrl !== subjectId && subjects.some((s) => s.id === fromUrl)) {
      setSubjectId(fromUrl);
    }
  }, [searchParams, subjectId, subjects]);

  const subject = getSubjectById(subjectId) ?? subjects[0];
  const useCases = subject ? getInteractiveUseCasesForSubject(subject.id) : [];
  const builderModes = subject ? getBuilderModesForSubject(subject.id) : [];

  const selectSubject = (id: string) => {
    setSubjectId(id);
    setAiNotice(null);
    const next = new URLSearchParams(searchParams);
    next.set('subject', id);
    setSearchParams(next, { replace: true });
  };

  const handleSelectRecipe = (recipe: AICaseRecipe) => {
    setSelectedRecipe(recipe);
    setAiNotice(null);
    setAiOpen(true);
  };

  const handleGenerate = async (options: RecipeAiBuildOptions) => {
    if (!selectedRecipe || !subject) return;
    setAiGenerating(true);
    setAiNotice(null);
    let generationNotice: string | null = null;

    try {
      let title = selectedRecipe.title;
      let payload: unknown;

      try {
        const generated = await generateSubjectAwareActivity({
          mode: selectedRecipe.mode,
          recipe: selectedRecipe,
          subjectId: subject.id,
          subjectTag: subject.tag,
          level: options.level,
          difficulty: options.difficulty,
          tuning: options.tuning,
          musicPrefs: options.musicPrefs,
        });
        title = generated.title;
        payload = generated.payload;
        if (generated.notice) {
          generationNotice = generated.notice;
          setAiNotice(generationNotice);
        }
      } catch (e) {
        setAiNotice(e instanceof Error ? e.message : 'No se pudo generar la actividad');
        return;
      }

      const draftId = createDraftId();
      const useWrappedExam = recipeUsesGuideBlockEditor(selectedRecipe);

      if (useWrappedExam) {
        const saved = saveRecipeAsWrappedExam({
          recipe: selectedRecipe,
          title,
          payload,
          subjectTag: subject.tag,
        });
        setAiOpen(false);
        navigate(
          buildRecipePostGeneratePath(selectedRecipe, { wrappedExamId: saved.id }),
          {
            state: {
              fromRecipeNativeBlock: true,
              recipeId: selectedRecipe.id,
              ...(generationNotice ? { generatedNotice: generationNotice } : {}),
            },
          },
        );
        return;
      }

      saveActivityDraft({
        id: draftId,
        mode: selectedRecipe.mode,
        title,
        subjectTag: subject.tag,
        payload,
      });

      setAiOpen(false);
      navigate(buildRecipePostGeneratePath(selectedRecipe, { draftId }), {
        state: generationNotice ? { generatedNotice: generationNotice } : undefined,
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleMultiGenerate = async (result: MultiToolActivityDialogResult) => {
    if (!subject) return;
    setMultiGenerating(true);
    setAiNotice(null);
    try {
      const { exam, notice } = await generateMultiToolInteractiveClass({
        subjectId: subject.id,
        subjectTag: subject.tag,
        modes: result.modes,
        momentCount: result.momentCount,
        level: result.level,
        difficulty: result.difficulty,
        tuning: result.tuning,
      });
      setMultiOpen(false);
      if (notice) setAiNotice(notice);
      navigate(
        buildStudioMaterialUrl({
          profile: 'interactive_class',
          subjectId: subject.id,
          examId: exam.id,
        }),
        { state: notice ? { generatedNotice: notice } : undefined },
      );
    } catch (e) {
      setAiNotice(e instanceof Error ? e.message : 'No se pudo armar la clase multi-herramienta');
    } finally {
      setMultiGenerating(false);
    }
  };

  if (!subject) {
    return (
      <div className="studio-page w-full min-w-0">
        <p className="text-[var(--sala-ink-muted)]">No hay asignaturas configuradas.</p>
      </div>
    );
  }

  return (
    <div className="studio-page w-full min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--studio-accent)]">
        Studio · Taller interactivo
      </p>
      <h1 className="mt-3 font-display text-3xl text-[var(--sala-ink)] sm:text-4xl">
        Dinámicas en <span className="text-[var(--sala-accent)]">vivo</span> por asignatura
      </h1>
      <p className="mt-3 max-w-2xl text-[var(--sala-ink-muted)]">
        Recetas y motores para la sala (proyectar, jugar, encuestar). Las guías, rúbricas y
        fichas imprimibles se crean dentro de cada asignatura o curso.
      </p>

      <div className="studio-subject-picker mt-6" role="tablist" aria-label="Asignatura">
        {subjects.map((item) => {
          const active = item.id === subject.id;
          const Icon = getSubjectLucideIcon(item.id);
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`studio-subject-picker__chip${active ? ' studio-subject-picker__chip--active' : ''}`}
              style={
                active
                  ? ({
                      '--subject-hue': item.hue,
                      '--subject-tint': item.tint,
                    } as React.CSSProperties)
                  : undefined
              }
              onClick={() => selectSubject(item.id)}
            >
              <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
              <span>{item.name}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <MultiToolActivityCta onClick={() => setMultiOpen(true)} />
      </div>

      <section className="mt-8">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--studio-accent)]">
              Recetas interactivas
            </p>
            <h2 className="mt-1 font-display text-xl text-[var(--sala-ink)]">
              ¿Qué quieres hacer en {subject.name}?
            </h2>
          </div>
          <p className="text-xs text-[var(--sala-ink-dim)]">{useCases.length} dinámicas</p>
        </div>

        {aiNotice ? (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
            {aiNotice}
          </div>
        ) : null}

        {useCases.length === 0 ? (
          <p className="text-sm text-[var(--sala-ink-muted)]">
            Todavía no hay recetas publicadas para esta asignatura.
          </p>
        ) : (
          <div className="use-case-grid mt-4">
            {useCases.map((useCase) => (
              <UseCaseCard
                key={useCase.id}
                useCase={useCase}
                subjectHue={subject.hue}
                onSelect={handleSelectRecipe}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mt-10">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Wrench className="h-4 w-4 text-[var(--sala-ink-dim)]" aria-hidden />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--sala-ink-dim)]">
              Constructores técnicos
            </p>
            <h2 className="font-display text-lg text-[var(--sala-ink)]">
              Abrir motor con contexto de {subject.name}
            </h2>
          </div>
        </div>
        <p className="mb-4 max-w-2xl text-sm text-[var(--sala-ink-muted)]">
          Entrás al editor con la asignatura ya fija: el botón <strong>Construir con IA</strong> usa
          ese contexto.
        </p>
        <div className="use-case-grid">
          {builderModes.map((mode) => {
            const count = drafts.filter(
              (d) => d.mode === mode && d.subjectTag?.toLowerCase().includes(subject.tag.slice(0, 4)),
            ).length;
            return (
              <ModeCard
                key={mode}
                mode={mode}
                layout="grid"
                to={`/studio/${mode}?subject=${subject.id}`}
                draftCount={count}
              />
            );
          })}
        </div>
      </section>

      <RecipeAiBuildDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        recipe={selectedRecipe}
        subjectLabel={subject.name}
        subjectId={subject.id}
        subjectTag={subject.tag}
        loading={aiGenerating}
        onConfirm={handleGenerate}
      />

      <MultiToolActivityDialog
        open={multiOpen}
        onOpenChange={setMultiOpen}
        subjectLabel={subject.name}
        availableModes={builderModes}
        loading={multiGenerating}
        onConfirm={handleMultiGenerate}
      />
    </div>
  );
}
